/**
 * LE SCELLEMENT D'UNE ARCHIVE FISCALE — la 6ᵉ sérialisation signée (CONFORMITE §2.3).
 *
 * POURQUOI ICI, ET PAS DANS LE BACK-OFFICE QUI EST LE SEUL À ARCHIVER. Les cinq autres chaînes
 * (pièce, duplicata, JET, grand total, justificatif) vivent dans ce cœur, et `verifyChain` aussi.
 * Poser la sixième ailleurs ferait pointer le dossier de conformité vers DEUX modules pour « nos
 * sérialisations signées » — exactement la divergence que le projet a déjà payée et résolue
 * (SPEC §14.9bis). Le coût est de quelques lignes pures dans l'APK, qui ne les appelle jamais.
 *
 * ⚠️ CE MODULE NE SIGNE PAS. Il rend la CHAÎNE à hacher ; la clé et l'algorithme restent chez
 * l'appelant — même règle que le reste du cœur (signataire injecté). La caisse signe au Keystore,
 * le back-office avec sa propre clé : les deux chaînes sont séparées et n'ont jamais à se vérifier
 * l'une l'autre.
 *
 * R13, feuille « Signature de l'archive fiscale » — TROIS positions, dans cet ordre :
 *   1. `TAG-ARC`      « Toutes les données contenues dans l'archive »
 *   2. `TAG-ARC-OEN`  « Indication du report de signature précédente »
 *   3. `TAG-ARC-SIG`  « Signature Electronique de l'archive précédente »
 *
 * ⚠️ 🔴 LE REPORT EST UNE VALEUR ÉNUMÉRÉE, PAS UN VIDE — et c'est une divergence ASSUMÉE avec la
 * convention des autres chaînes, où « signature précédente vide » vaut début de chaîne. La raison
 * tient au lecteur : une chaîne de pièces n'est lue que par notre vérificateur, l'archive est lue
 * par un auditeur, dans un tableur, sans nous (§6.9.4). Un champ vide y est indiscernable de
 * « nous avons perdu le report », et §6.9.3.2 exige précisément les « valeurs de codage des
 * rubriques utilisées (listes énumératives) ». On écrit donc le mot.
 */
import { assertFieldSafe } from './serialize';

/**
 * Version de la sérialisation d'ARCHIVE — distincte de `SERIALIZATION_VERSION`.
 * ⚠️ Deux domaines de version, à ne pas confondre : `SERIALIZATION_VERSION` est le
 * `ENC-TIK-TAG-VER` des pièces de caisse. Les fondre ferait lire un bump de la caisse comme un
 * changement de format d'archive, et obligerait à re-signer des archives que rien n'a touchées.
 */
export const ARCHIVE_SEAL_VERSION = '1';

/** Le report de signature précédente (`TAG-ARC-OEN`) — liste FERMÉE, citée telle quelle en §6.9.3. */
export const ARCHIVE_CARRY_OVER = {
  /** Première archive de la chaîne du magasin : il n'y a rien à reporter. */
  FIRST: 'PREMIERE',
  /** La signature de l'archive précédente est reportée ci-après. */
  CARRIED: 'REPORTEE',
} as const;

export type ArchiveCarryOver = (typeof ARCHIVE_CARRY_OVER)[keyof typeof ARCHIVE_CARRY_OVER];

/** Ce qu'il faut pour sceller une archive : son condensat, et à quoi elle se chaîne. */
export interface ArchiveSealData {
  /**
   * Condensat du MANIFESTE, qui porte l'empreinte de chaque fichier de l'archive.
   * ⚠️ C'est ainsi que `TAG-ARC` couvre « l'intégralité des données » (§6.11.3.8) avec UNE
   * signature : la couverture est transitive, pas minimale — R13 note [8] interdit depuis le
   * 31/12/2022 de ne signer qu'un résumé, et un manifeste qui cite TOUT n'en est pas un.
   */
  manifestHashHex: string;
  /** Signature de l'archive précédente du même magasin — `''` si et seulement si `PREMIERE`. */
  previousSignature: string;
}

const HEX64 = /^[0-9a-f]{64}$/;

/**
 * Chaîne signée d'une ARCHIVE (ordre EXACT R13) :
 * `condensatManifeste | reportSignaturePrecedente | signaturePrecedente`
 *
 * ⚠️ LA SIGNATURE PRÉCÉDENTE EST LE DERNIER CHAMP, comme dans les cinq autres chaînes : c'est ce
 * qui permet à `verifyChain` de vérifier celle-ci SANS la connaître (il exige que l'argument se
 * termine par `|<signature précédente>`). Déplacer ce champ rendrait l'archive invérifiable par
 * l'outil commun, et il faudrait en écrire un second.
 *
 * ⚠️ LE REPORT EST DÉDUIT, JAMAIS PASSÉ : deux paramètres qui doivent s'accorder finissent par se
 * contredire — on saurait alors dire `PREMIERE` en reportant une signature, ce qu'aucun contrôle
 * n'attraperait puisque les deux valeurs seraient scellées ensemble.
 */
export function serializeArchiveSeal(a: ArchiveSealData): string {
  if (!HEX64.test(a.manifestHashHex)) {
    throw new Error(`core-nf525: condensat de manifeste attendu en 64 caractères hexadécimaux minuscules (reçu ${JSON.stringify(a.manifestHashHex)})`);
  }
  const carryOver: ArchiveCarryOver = a.previousSignature === '' ? ARCHIVE_CARRY_OVER.FIRST : ARCHIVE_CARRY_OVER.CARRIED;
  return [
    a.manifestHashHex,
    carryOver,
    assertFieldSafe(a.previousSignature, 'signatureArchivePrecedente'),
  ].join('|');
}

/** Le report tel qu'il sera scellé — pour que le manifeste écrive LE MÊME MOT, sans le recalculer. */
export const archiveCarryOver = (previousSignature: string): ArchiveCarryOver =>
  previousSignature === '' ? ARCHIVE_CARRY_OVER.FIRST : ARCHIVE_CARRY_OVER.CARRIED;
